import { useTranslator } from '@bluebeam/react-i18n';
import BaseInput from 'components/inputs/BaseInput';
import DateDropdown from 'components/inputs/dropdowns/DateDropdown';
import SingleSelect from 'components/inputs/dropdowns/SingleSelect.func';
import Label from 'components/inputs/Label';
import OverlayFooter from 'components/Overlays/OverlayFooter';
import OverlayHeader from 'components/Overlays/OverlayHeader';
import OverlayStdContainer from 'components/Overlays/OverlayStdContainer';
import * as React from 'react';
import { RawUpdateSheet, UpdateSheet } from 'records/UpdateSheet';
import { useAppState } from 'hooks/useAppState';
import AppStoreContext from 'contexts/AppStoreContext';
import { UpdateSheetDiscipline } from 'records/UpdateSheetDiscipline';
import { UpdateMultipleDrawings } from 'records/interactions/UpdateMultipleDrawings';
import { useDisciplineSelect } from 'hooks/useDisciplineSelect';

interface Props {
  interaction: UpdateMultipleDrawings;
}

const UpdateMultipleDrawingsOverlay: React.FC<Props> = ({
  interaction,
}: Props) => {
  const { selectedPages, selectedProject, projects } = useAppState();
  const { actions } = React.useContext(AppStoreContext);
  const { translate } = useTranslator([
    'updateMultipleDrawingsOverlay',
    'buttons',
    'disciplineFilterSection',
    'formLabels',
    'dropdownOptions',
  ]);
  const { disciplineOptions, projectDisciplines } = useDisciplineSelect();

  const selectedPageIds = React.useMemo(() => {
    return selectedPages.keySeq().toArray();
  }, [selectedPages]);

  const [localUpdateSheet, setLocalUpdateSheet] = React.useState(
    new UpdateSheet({
      projectid: selectedProject,
      issueddate: Math.ceil(Date.now() / 1000),
      pageids: selectedPageIds,
      discipline: '',
    })
  );

  const handleChange = React.useCallback(
    (key: keyof RawUpdateSheet, val: number | string): void => {
      setLocalUpdateSheet((prevLocalSheet) => {
        const nextLocalSheet = prevLocalSheet.set(key, val);

        const sheetDiscipline = projectDisciplines.get(
          nextLocalSheet.get('discipline', '')
        );

        return nextLocalSheet.set(
          'sheetDiscipline',
          sheetDiscipline
            ? new UpdateSheetDiscipline({
                id: sheetDiscipline.id,
                designator: sheetDiscipline.designator,
                description: translate(
                  `disciplineFilterSection:disciplines.${sheetDiscipline.description}` as `${string}:${string}`
                ),
              })
            : undefined
        );
      });
    },
    [projectDisciplines]
  );

  const handleCancelClick = React.useCallback((): void => {
    actions.clearInteractions();
  }, []);

  const handleSave = React.useMemo(
    () => async () => {
      await actions.updateSheetVersionInfo(
        new UpdateSheet({
          discipline: localUpdateSheet.discipline,
          sheetDiscipline: localUpdateSheet.sheetDiscipline,
          issueddate: localUpdateSheet.issueddate,
          revisioncomment: localUpdateSheet.revisioncomment,
          projectid: localUpdateSheet.projectid,
          pageids: selectedPageIds,
        })
      );
      await actions.getSheets();

      // Close Overlay
      await actions.clearInteractions();
    },
    [localUpdateSheet, selectedPageIds]
  );

  const getCurrentProject = () => {
    return projects.get(selectedProject);
  };

  if (!getCurrentProject()) {
    return null;
  }

  const disciplineSelection = React.useMemo(() => {
    return disciplineOptions.has(localUpdateSheet.discipline)
      ? disciplineOptions.get(localUpdateSheet.discipline)
      : null;
  }, [disciplineOptions, localUpdateSheet.discipline]);

  return (
    <OverlayStdContainer
      id="update-multiple-drawings-overlay"
      style={{ width: '336px' }}
    >
      <OverlayHeader
        id="update-multiple-drawings-overlay-header"
        title={translate('updateMultipleDrawingsOverlay:editHeader', {
          count: selectedPages.size,
        })}
        onCancelClick={handleCancelClick}
      />
      <div
        className="column justify-between oa"
        style={{
          height: 'calc(100% - 48px)',
        }}
      >
        <div className="m4">
          <div className="mb4">
            <Label
              label={translate('updateMultipleDrawingsOverlay:discipline')}
            />
            <SingleSelect
              id="update-multi-pub-req-overlay-discipline"
              // isClearable
              placeholder={translate('dropdownOptions:chooseDiscipline')}
              value={disciplineSelection}
              options={Array.from(disciplineOptions.values())}
              onChange={(selectedOption): void =>
                handleChange(
                  'discipline',
                  selectedOption.value ? selectedOption.value : ''
                )
              }
            />
          </div>
          <div className="mb4">
            <Label
              label={translate('updateMultipleDrawingsOverlay:issuedDate')}
            />
            <DateDropdown
              id="update-multiple-drawings-date"
              date={localUpdateSheet.issueddate}
              onChange={(date: Date): void => {
                handleChange('issueddate', date.getTime() / 1000);
              }}
            />
          </div>
          <div className="mb4">
            <Label
              label={translate('updateMultipleDrawingsOverlay:revisionComment')}
            />
            <BaseInput
              id="update-multiple-drawings-comment"
              onChange={(evt): void =>
                handleChange('revisioncomment', evt.target.value)
              }
            />
          </div>
        </div>
        <OverlayFooter
          id="update-multiple-drawings-footer"
          confirmText={translate('buttons:save')}
          onCancelClick={handleCancelClick}
          onConfirmClick={handleSave}
        />
      </div>
    </OverlayStdContainer>
  );
};
export default UpdateMultipleDrawingsOverlay;
